import React from 'react';
import PropTypes from 'prop-types';
import styles from './CanvasContent.css';
import classNames from 'classnames';
import testable from '@xingternal/malt-util/testable';
import { QA_MALT } from '@xingternal/malt-constants';

const TestableDiv = testable(QA_MALT, 'div');

/**
 * Alignment and spacing helper for `<Canvas />`
 */
export default function CanvasContent({
  children,
  centered,
  scroll,
  fill,
  className,
  addTopSpace,
  testId,
}) {
  const CanvasContentClasses = classNames(
    styles.base,
    {
      [styles.scroll]: scroll,
      [styles.centered]: centered,
      [styles.fill]: fill,
      [styles.topSpace]: !fill && addTopSpace,
    },
    className
  );

  return (
    <TestableDiv className={CanvasContentClasses} testId={testId}>
      {children}
    </TestableDiv>
  );
}

CanvasContent.propTypes = {
  /** The actual content */
  children: PropTypes.node,
  /** Determines whether the content is vertically and horizontally centered in the viewport */
  centered: PropTypes.bool,
  /** `fill={true}` will remove spacing */
  fill: PropTypes.bool,
  /** A custom className for the content */
  className: PropTypes.string,
  /**
   * Whether the content should introduce a new scroll-bar.
   * This should not be set manually but rather be used with the `inScrollContext` higher-order component.
   *
   * ```js
   * import { inScrollContext } from '@xingternal/malt-util';
   * import { CanvasContent } from '@xingternal/malt-canvas';
   *
   * const ScrollAwareContent = inScrollContext(CanvasContent);
   * // ...
   * ```
   */
  scroll: PropTypes.bool,
  /** @private */
  addTopSpace: PropTypes.bool,
  /** define a testId for the canvas content */
  testId: PropTypes.string,
};

CanvasContent.displayName = 'CanvasContent';

CanvasContent.defaultProps = {
  centered: true,
  scroll: false,
  fill: false,
};
